import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FolderPlus, FolderKanban, MapPin, Building2, HardHat, 
  FileSpreadsheet, FlaskConical, Eye, FileText, ArrowLeft, 
  Trash2, CheckCircle, Plus, X, DownloadCloud 
} from 'lucide-react';
import { useProject } from '../../context/ProjectContext';
import { RecordService } from '../../core/application/RecordService';
import { dateUtils } from '../../core/utils/dateUtils';
import { backupEngine } from '../../core/backup/backupEngine';
import { storageQuota } from '../../core/utils/storageQuota';

export const ProjectWorkspace = () => {
  const navigate = useNavigate();
  const { projects, activeProject, selectActiveProject, createNewProject, removeProject } = useProject();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectStats, setProjectStats] = useState({});

  const [formData, setFormData] = useState({
    title: '', location: '', contractor: '', consultant: '', client: ''
  });

  useEffect(() => {
    storageQuota.requestPersistence();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      const statsMap = {};
      for (const p of projects) {
        const recs = await RecordService.getActiveRecords(p.id);
        statsMap[p.id] = {
          sheets: recs.filter(r => r.type === 'quantitySheet').length,
          soil: recs.filter(r => r.type === 'soilTest').length,
          scans: recs.filter(r => r.type === 'crackScan').length,
          docs: recs.filter(r => r.type === 'document').length
        };
      }
      setProjectStats(statsMap);
    };
    if (projects.length > 0) loadStats();
  }, [projects]);

  const handleExportProject = async (e, id) => {
    e.stopPropagation();
    try {
      await backupEngine.exportProjectPackage(id);
    } catch (err) {
      alert("خطا در تهیه فایل پشتیبان: " + err.message);
    }
  };

  const handleEnterProject = async (id) => {
    await selectActiveProject(id);
    navigate('/tools/engineering');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.title) return;
    await createNewProject(formData);
    setFormData({ title: '', location: '', contractor: '', consultant: '', client: '' });
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6 font-sans">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/10 [body.light-theme_&]:border-slate-200 pb-4">
        <div>
          <h2 className="text-xl font-black text-white [body.light-theme_&]:text-slate-900 flex items-center gap-2.5">
            <FolderKanban className="w-6 h-6 text-amber-500" />
            <span>Project Workspace | میزکار پروژه</span>
          </h2>
          <p className="text-xs text-slate-400 [body.light-theme_&]:text-slate-600 mt-1">
            پرونده دیجیتال کارگاه‌ها؛ مدیریت یکپارچه محاسبات، آزمایش‌ها و پایش‌های مهندسی
          </p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black rounded-xl text-xs transition shadow-lg shadow-amber-500/20 active:scale-95">
          <Plus className="w-4 h-4 stroke-[3]" />
          <span>ایجاد پروژه کارگاهی جدید</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {projects.map((proj) => {
          const isActive = activeProject?.id === proj.id;
          const stats = projectStats[proj.id] || { sheets: 0, soil: 0, scans: 0, docs: 0 };
          const meta = proj.metadata || {};

          return (
            <div key={proj.id} onClick={() => handleEnterProject(proj.id)} className={`group cursor-pointer rounded-2xl p-5 sm:p-6 transition-all border flex flex-col justify-between ${isActive ? 'bg-slate-900/90 [body.light-theme_&]:bg-white border-amber-500/80 shadow-[0_0_25px_rgba(245,158,11,0.15)]' : 'bg-slate-900/40 [body.light-theme_&]:bg-white/70 border-white/10 [body.light-theme_&]:border-slate-200 hover:border-amber-500/40'}`}>
              <div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 mb-2">
                      افتتاح: {dateUtils.formatJalali(proj.createdAt)}
                    </span>
                    <h3 className="text-base sm:text-lg font-extrabold text-white [body.light-theme_&]:text-slate-900 leading-snug">
                      {meta.title || 'پروژه عمرانی'}
                    </h3>
                  </div>

                  {/* راه حل خطای JSX: قرار دادن دکمه‌ها داخل یک Div */}
                  <div className="flex items-center gap-1 relative z-10">
                    <button onClick={(e) => handleExportProject(e, proj.id)} className="text-slate-400 hover:text-amber-400 p-1.5 transition" title="دریافت فایل پشتیبان کامل پروژه (.cnex.json)">
                      <DownloadCloud className="w-4 h-4" />
                    </button>
                    {projects.length > 1 && (
                      <button onClick={(e) => { e.stopPropagation(); if (window.confirm('آیا از حذف این پروژه اطمینان دارید؟')) removeProject(proj.id); }} className="text-slate-500 hover:text-rose-400 p-1.5 transition" title="حذف پروژه">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5 text-xs text-slate-300 [body.light-theme_&]:text-slate-600 mb-6 bg-slate-950/40 [body.light-theme_&]:bg-slate-50 p-3 rounded-xl border border-white/5 [body.light-theme_&]:border-slate-200">
                  {meta.location && (<div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-rose-400 shrink-0" /><span>{meta.location}</span></div>)}
                  {meta.contractor && (<div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-sky-400 shrink-0" /><span>پیمانکار: {meta.contractor}</span></div>)}
                  {meta.consultant && (<div className="flex items-center gap-2"><HardHat className="w-3.5 h-3.5 text-amber-400 shrink-0" /><span>مشاور: {meta.consultant}</span></div>)}
                </div>

                <div className="grid grid-cols-4 gap-2 text-center text-xs mb-5">
                  <div className="bg-slate-800/60 [body.light-theme_&]:bg-slate-100 p-2 rounded-lg"><FileSpreadsheet className="w-4 h-4 text-sky-400 mx-auto mb-1" /><div className="font-mono font-bold text-slate-200 [body.light-theme_&]:text-slate-800">{stats.sheets}</div><div className="text-[10px] text-slate-400">صورت‌جلسه</div></div>
                  <div className="bg-slate-800/60 [body.light-theme_&]:bg-slate-100 p-2 rounded-lg"><FlaskConical className="w-4 h-4 text-emerald-400 mx-auto mb-1" /><div className="font-mono font-bold text-slate-200 [body.light-theme_&]:text-slate-800">{stats.soil}</div><div className="text-[10px] text-slate-400">تراکم خاک</div></div>
                  <div className="bg-slate-800/60 [body.light-theme_&]:bg-slate-100 p-2 rounded-lg"><Eye className="w-4 h-4 text-purple-400 mx-auto mb-1" /><div className="font-mono font-bold text-slate-200 [body.light-theme_&]:text-slate-800">{stats.scans}</div><div className="text-[10px] text-slate-400">پایش ترک</div></div>
                  <div className="bg-slate-800/60 [body.light-theme_&]:bg-slate-100 p-2 rounded-lg"><FileText className="w-4 h-4 text-amber-400 mx-auto mb-1" /><div className="font-mono font-bold text-slate-200 [body.light-theme_&]:text-slate-800">{stats.docs}</div><div className="text-[10px] text-slate-400">اسناد فنی</div></div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-white/10 [body.light-theme_&]:border-slate-200">
                <span className="text-xs font-bold text-amber-500 flex items-center gap-1.5">
                  {isActive ? (
                    <><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400 font-bold">پروژه فعال سیستم</span></>
                  ) : (<span>انتخاب به عنوان پروژه جاری</span>)}
                </span>
                <div className="flex items-center gap-1 text-xs font-black text-sky-400 group-hover:gap-2 transition-all">
                  <span>ورود به کارگاه</span><ArrowLeft className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="bg-[#0b1329] [body.light-theme_&]:bg-white border border-slate-700 [body.light-theme_&]:border-slate-200 rounded-3xl p-6 w-full max-w-lg shadow-2xl relative">
            <button onClick={() => setIsModalOpen(false)} className="absolute left-4 top-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center"><FolderPlus className="w-5 h-5" /></div>
              <h3 className="font-black text-base text-white [body.light-theme_&]:text-slate-900">افتتاح پرونده کارگاهی جدید</h3>
            </div>
            <form onSubmit={handleCreate} className="space-y-3.5 text-xs">
              <div>
                <label className="text-slate-300 [body.light-theme_&]:text-slate-700 block mb-1 font-bold">عنوان پروژه <span className="text-rose-400">*</span>:</label>
                <input required placeholder="مثال: پروژه احداث پل" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-50 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-xl p-3 text-white [body.light-theme_&]:text-slate-900 focus:outline-none focus:border-amber-500" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">کارفرما:</label><input value={formData.client} onChange={(e) => setFormData({ ...formData, client: e.target.value })} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-50 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-xl p-2.5 text-white [body.light-theme_&]:text-slate-900 focus:outline-none" /></div>
                <div><label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">مشاور:</label><input value={formData.consultant} onChange={(e) => setFormData({ ...formData, consultant: e.target.value })} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-50 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-xl p-2.5 text-white [body.light-theme_&]:text-slate-900 focus:outline-none" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">پیمانکار:</label><input value={formData.contractor} onChange={(e) => setFormData({ ...formData, contractor: e.target.value })} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-50 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-xl p-2.5 text-white [body.light-theme_&]:text-slate-900 focus:outline-none" /></div>
                <div><label className="text-slate-400 [body.light-theme_&]:text-slate-600 block mb-1">کیلومتراژ:</label><input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full bg-slate-900 [body.light-theme_&]:bg-slate-50 border border-slate-700 [body.light-theme_&]:border-slate-300 rounded-xl p-2.5 text-white [body.light-theme_&]:text-slate-900 focus:outline-none" /></div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-800 [body.light-theme_&]:border-slate-200">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white font-bold">انصراف</button>
                <button type="submit" className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl font-black">ثبت و افتتاح پرونده</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectWorkspace;
